import React, { useState } from "react";
import { FaPills, FaChevronDown, FaTh, FaList } from "react-icons/fa";
import { RiCloseLine } from "react-icons/ri";
import { MdCategory, MdOutlineClear } from "react-icons/md";
import { FiFilter } from "react-icons/fi";

const getThemeColorBg = (themeColor) => {
    if (themeColor === 'primary') return 'bg-primary';
    if (themeColor === 'emerald') return 'bg-emerald-600';
    if (themeColor === 'purple') return 'bg-purple-600';
    return 'bg-primary';
}

const getThemeColorText = (themeColor) => {
    if (themeColor === 'primary') return 'text-primary';
    if (themeColor === 'emerald') return 'text-emerald-600';
    if (themeColor === 'purple') return 'text-purple-600';
    return 'text-primary';
}

const getThemeColorHover = (themeColor) => {
    if (themeColor === 'primary') return 'hover:bg-primary/5';
    if (themeColor === 'emerald') return 'hover:bg-emerald-600/5';
    if (themeColor === 'purple') return 'hover:bg-purple-600/5';
    return 'hover:bg-primary/5';
}

const getThemeColorHoverBg = (themeColor) => {
    if (themeColor === 'primary') return 'hover:bg-primary/90';
    if (themeColor === 'emerald') return 'hover:bg-emerald-700';
    if (themeColor === 'purple') return 'hover:bg-purple-700';
    return 'hover:bg-primary/90';
}

const getThemeColorFocus = (themeColor) => {
    if (themeColor === 'primary') return 'focus:ring-primary';
    if (themeColor === 'emerald') return 'focus:ring-emerald-600';
    if (themeColor === 'purple') return 'focus:ring-purple-600';
    return 'focus:ring-primary';
}

const getThemeColorBgOpacity = (themeColor, opacity) => {
    if (themeColor === 'primary') return `bg-primary/${opacity}`;
    if (themeColor === 'emerald') return `bg-emerald-600/${opacity}`;
    if (themeColor === 'purple') return `bg-purple-600/${opacity}`;
    return `bg-primary/${opacity}`;
}

const getThemeColorHoverText = (themeColor) => {
    if (themeColor === 'primary') return 'hover:text-primary';
    if (themeColor === 'emerald') return 'hover:text-emerald-600';
    if (themeColor === 'purple') return 'hover:text-purple-600';
    return 'hover:text-primary';
}

// FilterDropdown component
const FilterDropdown = ({
    title,
    icon: Icon,
    options,
    selectedItems,
    onToggle,
    isOpen,
    onToggleOpen,
    placeholder = "Select options...",
    themeColor = 'primary'
}) => (
    <div className="">
        <div className="flex items-center gap-2 mb-2">
            <Icon className={`size-4 ${getThemeColorText(themeColor)}`} />
            <h4 className="text-14 font-medium text-secondary">{title}</h4>
            {selectedItems?.length > 0 && (
                <span className={`px-1.5 py-0.5 text-12 ${getThemeColorBg(themeColor)} text-white rounded-full`}>
                    {selectedItems?.length}
                </span>
            )}
        </div>

        <div className="relative">
            <button
                onClick={onToggleOpen}
                className="w-full flex items-center justify-between p-2 text-left border rounded-md bg-white/60 border-white/70 hover:bg-white/80 transition-colors"
            >
                <span className={`text-14 ${selectedItems?.length > 0 ? 'text-secondary' : 'text-secondary/60'}`}>
                    {selectedItems?.length > 0
                        ? `${selectedItems?.length} selected`
                        : placeholder
                    }
                </span>
                <FaChevronDown className={`size-3 text-secondary/50 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto border rounded-md bg-white/95 backdrop-blur-sm border-white/70 shadow-lg">
                    <div className="p-2 space-y-1">
                        {options.map(option => (
                            <label key={option} className={`flex items-center gap-2 p-1 cursor-pointer ${getThemeColorHover(themeColor)} rounded`}>
                                <input
                                    type="checkbox"
                                    checked={selectedItems.includes(option)}
                                    onChange={() => onToggle(option)}
                                    className={`size-3 ${getThemeColorText(themeColor)} ${getThemeColorFocus(themeColor)} rounded`}
                                />
                                <span className="text-14 text-secondary/80 truncate">{option}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>

        {/* Selected items display */}
        {selectedItems?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
                {selectedItems.map(item => (
                    <span key={item} className={`inline-flex items-center gap-1 px-2 py-1 text-14 ${getThemeColorBgOpacity(themeColor, '10')} ${getThemeColorText(themeColor)} rounded-full`}>
                        {item}
                        <button
                            onClick={() => onToggle(item)}
                            className={`${getThemeColorBgOpacity(themeColor, '20')} rounded-full p-0.5`}
                        >
                            <RiCloseLine className="size-3" />
                        </button>
                    </span>
                ))}
            </div>
        )}
    </div>
);

// Display Format Selector component
const DisplayFormatSelector = ({ displayFormat, onDisplayFormatChange, themeColor = 'primary' }) => (
    <div className="flex gap-1 p-1.5 border rounded-lg bg-white/60 border-white/70">
        <button
            onClick={() => onDisplayFormatChange('grid')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-md text-14 font-medium transition-all ${displayFormat === 'grid'
                ? `${getThemeColorBg(themeColor)} text-white shadow-sm`
                : 'text-secondary/70 hover:text-secondary hover:bg-white/80'
                }`}
        >
            <FaTh className="size-3.5" />
            <span className="hidden sm:inline">Grid</span>
        </button>

        <button
            onClick={() => onDisplayFormatChange('list')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-md text-14 font-medium transition-all ${displayFormat === 'list'
                ? `${getThemeColorBg(themeColor)} text-white shadow-sm`
                : 'text-secondary/70 hover:text-secondary hover:bg-white/80'
                }`}
        >
            <FaList className="size-3.5" />
            <span className="hidden sm:inline">List</span>
        </button>
    </div>
);

// Mobile Filter Button component
const MobileFilterButton = ({ onClick, hasActiveFilters, activeFiltersCount, themeColor = 'primary' }) => (
    <button
        onClick={onClick}
        className={`flex-shrink-0 h-8 px-3 flex items-center gap-2 rounded-lg ${getThemeColorBg(themeColor)} text-white ${getThemeColorHoverBg(themeColor)} transition-colors`}
    >
        <FiFilter className="size-3" />
        <span className="text-sm font-medium">Filter</span>
        {hasActiveFilters && (
            <span className="px-1.5 py-0.5 text-10 bg-white/20 text-white rounded-full">
                {activeFiltersCount}
            </span>
        )}
    </button>
);

// Desktop Sidebar component
const DesktopSidebar = ({
    filterOptions,
    selectedCategories,
    selectedForms,
    displayFormat,
    openCategoryDropdown,
    openFormDropdown,
    hasActiveFilters,
    onCategoryToggle,
    onFormToggle,
    onCategoryDropdownToggle,
    onFormDropdownToggle,
    onDisplayFormatChange,
    onClearAllFilters,
    themeColor = 'primary',
    children
}) => (
    <div className="w-1/4 min-w-[220px] sticky top-20 hidden md:block self-start">
        {/* Filter Header */}
        <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
                <FiFilter className={`size-4 ${getThemeColorText(themeColor)}`} />
                <h3 className="text-16 font-medium text-secondary">Filters & Display</h3>
            </div>

            {hasActiveFilters && (
                <button
                    onClick={onClearAllFilters}
                    className={`flex items-center gap-1 px-2 py-1 text-14 text-secondary/70 ${getThemeColorHoverText(themeColor)} transition-colors`}
                >
                    <MdOutlineClear className="size-3" />
                    Clear
                </button>
            )}
        </div>

        {/* Display Format Selector */}
        <DisplayFormatSelector 
            displayFormat={displayFormat} 
            onDisplayFormatChange={onDisplayFormatChange}
            themeColor={themeColor}
        />

        {/* Category Filters */}
        {filterOptions?.categories?.length > 0 ? (
            <div className="flex flex-col gap-3 my-4 p-4 border rounded-lg bg-white/40 backdrop-blur-sm border-white/60">
                {filterOptions?.categories?.length > 0 && (
                    <FilterDropdown
                        title="Categories"
                        icon={MdCategory}
                        options={filterOptions.categories}
                        selectedItems={selectedCategories}
                        onToggle={onCategoryToggle}
                        isOpen={openCategoryDropdown}
                        onToggleOpen={onCategoryDropdownToggle}
                        placeholder="Select categories..."
                        themeColor={themeColor}
                    />
                )}

                {/* Form Filters */}
                {filterOptions?.forms?.length > 0 && (
                    <FilterDropdown
                        title="Forms"
                        icon={FaPills}
                        options={filterOptions.forms}
                        selectedItems={selectedForms}
                        onToggle={onFormToggle}
                        isOpen={openFormDropdown}
                        onToggleOpen={onFormDropdownToggle}
                        placeholder="Select forms..."
                        themeColor={themeColor}
                    />
                )}
            </div>
        ) : (
            <div className="flex flex-col gap-2 mt-3 mb-4 p-3 border rounded-lg bg-white/40 backdrop-blur-sm border-white/60">
                <h4 className="text-14 font-light text-secondary/80">No filters available.</h4>
            </div>)}
        
        {children}
    </div>
);

// Mobile Filter Drawer component
const MobileFilterDrawer = ({
    isOpen,
    onClose,
    filterOptions,
    selectedCategories,
    selectedForms,
    hasActiveFilters,
    onCategoryToggle,
    onFormToggle,
    onClearAllFilters,
    themeColor = 'primary'
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 md:hidden backdrop-blur-sm h-[100vh] z-50">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm h-full"
                onClick={onClose}
            />

            {/* Drawer */}
            <div className="fixed right-0 top-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out md:hidden">
                <div className="flex flex-col h-full">
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-gray-200">
                        <div className="flex items-center gap-2">
                            <FiFilter className={`size-5 ${getThemeColorText(themeColor)}`} />
                            <h3 className="text-lg font-semibold text-gray-800">Filters & Display</h3>
                            {hasActiveFilters && (
                                <span className={`px-2 py-1 text-xs ${getThemeColorBg(themeColor)} text-white rounded-full`}>
                                    {selectedCategories?.length + selectedForms?.length}
                                </span>
                            )}
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <RiCloseLine className="size-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-6 h-full">
                        {/* Categories Filter */}
                        {filterOptions?.categories?.length > 0 && (
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-sm font-medium text-gray-700">Categories</h4>
                                    {selectedCategories?.length > 0 && (
                                        <span className={`px-2 py-1 text-xs ${getThemeColorBg(themeColor)} text-white rounded-full`}>
                                            {selectedCategories?.length}
                                        </span>
                                    )}
                                </div>
                                <div className="space-y-2 max-h-[30vh] overflow-y-auto">
                                    {filterOptions?.categories?.map(category => (
                                        <label key={category} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={selectedCategories.includes(category)}
                                                onChange={() => onCategoryToggle(category)}
                                                className={`size-4 ${getThemeColorText(themeColor)} ${getThemeColorFocus(themeColor)} rounded`}
                                            />
                                            <span className="text-sm text-gray-700">{category}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Forms Filter */}
                        {filterOptions?.forms?.length > 0 && (
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-sm font-medium text-gray-700">Forms</h4>
                                    {selectedForms?.length > 0 && (
                                        <span className={`px-2 py-1 text-xs ${getThemeColorBg(themeColor)} text-white rounded-full`}>
                                            {selectedForms?.length}
                                        </span>
                                    )}
                                </div>
                                <div className="space-y-2 max-h-[30vh] overflow-y-auto">
                                    {filterOptions?.forms?.map(form => (
                                        <label key={form} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={selectedForms.includes(form)}
                                                onChange={() => onFormToggle(form)}
                                                className={`size-4 ${getThemeColorText(themeColor)} ${getThemeColorFocus(themeColor)} rounded`}
                                            />
                                            <span className="text-sm text-gray-700">{form}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-4 border-t border-gray-200 space-y-3">
                        {hasActiveFilters && (
                            <button
                                onClick={onClearAllFilters}
                                className="w-full py-3 px-4 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                                Clear All Filters
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className={`w-full py-3 px-4 text-sm font-medium text-white ${getThemeColorBg(themeColor)} ${getThemeColorHoverBg(themeColor)} rounded-lg transition-colors`}
                        >
                            Apply Filters
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Display Only component
const DisplayOnly = ({ displayFormat, onDisplayFormatChange, themeColor = 'primary' }) => (
    <div className="flex items-center gap-2">
        <DisplayFormatSelector 
            displayFormat={displayFormat} 
            onDisplayFormatChange={onDisplayFormatChange}
            themeColor={themeColor}
        />
    </div>
);

// Main FilterAndDisplay component
export default function FilterAndDisplay({
    // Mode prop to determine what to render
    mode = 'sidebar', // 'button', 'sidebar', 'drawer', 'display-only', 'sidebar-display-only'
    themeColor = 'primary', // 'emerald', 'purple'
    
    // Filter options
    filterOptions,
    
    // Filter states
    selectedCategories,
    selectedForms,
    displayFormat,
    
    // Dropdown states
    openCategoryDropdown,
    openFormDropdown,
    
    // Mobile filter drawer state
    isMobileFilterOpen,
    
    // Event handlers
    onCategoryToggle,
    onFormToggle,
    onCategoryDropdownToggle,
    onFormDropdownToggle,
    onDisplayFormatChange,
    onClearAllFilters,
    onMobileFilterToggle,
    
    // Children for sidebar content
    children
}) {
    const hasActiveFilters = selectedCategories?.length > 0 || selectedForms?.length > 0 || displayFormat !== 'grid';
    const activeFiltersCount = selectedCategories?.length + selectedForms?.length;

    // Render based on mode
    switch (mode) {
        case 'display-only':
            return (
                <DisplayOnly
                    displayFormat={displayFormat}
                    onDisplayFormatChange={onDisplayFormatChange}
                    themeColor={themeColor}
                />
            );
        
        case 'button':
            return (
                <MobileFilterButton 
                    onClick={onMobileFilterToggle}
                    hasActiveFilters={hasActiveFilters}
                    activeFiltersCount={activeFiltersCount}
                    themeColor={themeColor}
                />
            );
        
        case 'sidebar':
            return (
                <DesktopSidebar
                    filterOptions={filterOptions}
                    selectedCategories={selectedCategories}
                    selectedForms={selectedForms}
                    displayFormat={displayFormat}
                    openCategoryDropdown={openCategoryDropdown}
                    openFormDropdown={openFormDropdown}
                    hasActiveFilters={hasActiveFilters}
                    onCategoryToggle={onCategoryToggle}
                    onFormToggle={onFormToggle}
                    onCategoryDropdownToggle={onCategoryDropdownToggle}
                    onFormDropdownToggle={onFormDropdownToggle}
                    onDisplayFormatChange={onDisplayFormatChange}
                    onClearAllFilters={onClearAllFilters}
                    themeColor={themeColor}
                >
                    {children}
                </DesktopSidebar>
            );
        
        case 'drawer':
            return (
                <MobileFilterDrawer
                    isOpen={isMobileFilterOpen}
                    onClose={onMobileFilterToggle}
                    filterOptions={filterOptions}
                    selectedCategories={selectedCategories}
                    selectedForms={selectedForms}
                    hasActiveFilters={hasActiveFilters}
                    onCategoryToggle={onCategoryToggle}
                    onFormToggle={onFormToggle}
                    onClearAllFilters={onClearAllFilters}
                    themeColor={themeColor}
                />
            );
        
        case 'sidebar-display-only':
            return (
                <DesktopSidebar
                    filterOptions={filterOptions}
                    selectedCategories={selectedCategories}
                    selectedForms={selectedForms}
                    themeColor={themeColor}
                />
            );
        
        default:
            return null;
    }
}

// Export individual components for flexibility
export { FilterDropdown, DisplayFormatSelector, MobileFilterButton, DesktopSidebar, MobileFilterDrawer, DisplayOnly }; 